import { useState, useRef, useEffect, useContext } from "react";
import { 
  Video, User, Ban, VolumeX, MessageSquare, UserPlus, Navigation,
  DollarSign, Send, Gamepad2, Phone, Paperclip,
  ChevronDown, Eye, EyeOff, UserX, Flag
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../App";

const UserActionMenu = ({ 
  targetUser, 
  onClose, 
  onStartPM,
  onRequestWebcam,
  onInviteToCam,
  onViewProfile,
  onInviteToRoom,
  onTip,
  onInviteToGame,
  onInviteVideoCall,
  onSendFile,
  onFollow,
  onReport,
  isIgnored: initialIgnored = false,
  onIgnoreChanged,
  position = { x: 0, y: 0 },
  showInline = false 
}) => {
  const { token, user } = useContext(AuthContext);
  const menuRef = useRef(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isIgnored, setIsIgnored] = useState(initialIgnored);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Don't show menu for own user
  if (targetUser?.user_id === user?.user_id) {
    return null;
  }

  const handleRequestWebcam = async () => {
    try {
      onRequestWebcam?.(targetUser);
      toast.info(`Webcam request sent to ${targetUser.username}`);
      onClose?.();
    } catch (error) {
      toast.error("Failed to send webcam request");
    }
  };

  const handleViewProfile = () => {
    onViewProfile?.(targetUser);
    onClose?.();
  };

  const handleStartPM = () => {
    onStartPM?.(targetUser);
    onClose?.();
  };

  const handleBlock = async () => {
    try {
      await axios.post(`${API}/users/block/${targetUser.user_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsBlocked(true);
      toast.success(`${targetUser.username} has been blocked`);
      onClose?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Failed to block user";
      toast.error(msg);
    }
  };

  const handleUnblock = async () => {
    try {
      await axios.post(`${API}/users/unblock/${targetUser.user_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsBlocked(false);
      toast.success(`${targetUser.username} has been unblocked`);
      onClose?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to unblock user");
    }
  };

  const handleIgnore = async () => {
    try {
      if (isIgnored) {
        await axios.post(`${API}/users/unignore/${targetUser.user_id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsIgnored(false);
        toast.success(`${targetUser.username} is no longer ignored`);
        onIgnoreChanged?.(targetUser, false);
      } else {
        await axios.post(`${API}/users/ignore/${targetUser.user_id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsIgnored(true);
        toast.success(`${targetUser.username} is now ignored`);
        onIgnoreChanged?.(targetUser, true);
      }
      onClose?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update ignore");
    }
  };

  const handleAddFriend = async () => {
    try {
      await axios.post(`${API}/friends/request`, 
        { friend_user_id: targetUser.user_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Friend request sent to ${targetUser.username}!`);
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send friend request");
    }
  };

  const handleInviteToRoom = () => {
    if (onInviteToRoom) {
      onInviteToRoom(targetUser);
    } else {
      toast.info("Invite action not available in this context");
    }
    onClose?.();
  };

  const menuItems = [
    {
      icon: MessageSquare,
      label: "Instant Message",
      onClick: handleStartPM,
      color: "text-blue-600"
    },
    {
      icon: UserPlus,
      label: "Add Friend",
      onClick: handleAddFriend,
      color: "text-emerald-600"
    },
    {
      icon: Navigation,
      label: "Follow",
      onClick: () => { onFollow?.(targetUser) ?? toast.info("Add this user as a friend first to follow them."); onClose?.(); },
      color: "text-cyan-600"
    },
    {
      icon: User,
      label: "View Profile",
      onClick: handleViewProfile,
      color: "text-green-600"
    },
    {
      icon: DollarSign,
      label: "Send Tip",
      onClick: () => { onTip?.(targetUser) ?? toast.info("Open Account → Send Tip to send diamonds to this user."); onClose?.(); },
      color: "text-yellow-600"
    },
    { divider: true },
    {
      icon: Send,
      label: "Invite to Room",
      onClick: handleInviteToRoom,
      color: "text-indigo-600"
    },
    {
      icon: Gamepad2,
      label: "Invite to Game",
      onClick: () => { onInviteToGame?.(targetUser) ?? toast.info("Game invites coming soon — pick a game first."); onClose?.(); },
      color: "text-pink-600"
    },
    {
      icon: Phone,
      label: "Invite Video Call",
      onClick: () => { onInviteVideoCall?.(targetUser) ?? toast.info("Video call invites coming soon."); onClose?.(); },
      color: "text-purple-700"
    },
    {
      icon: Paperclip,
      label: "Send File",
      onClick: () => { onSendFile?.(targetUser) ?? toast.info("Open the IM window with this user, then click the 📎 paperclip."); onClose?.(); },
      color: "text-slate-600"
    },
    {
      icon: Video,
      label: "Request Webcam",
      onClick: handleRequestWebcam,
      color: "text-purple-600"
    },
    {
      icon: Video,
      label: "Invite to View Cam",
      onClick: () => { onInviteToCam?.(targetUser); onClose?.(); },
      color: "text-pink-600"
    },
    { divider: true },
    {
      icon: isBlocked ? Eye : Ban,
      label: isBlocked ? "Unblock User" : "Block User",
      onClick: isBlocked ? handleUnblock : handleBlock,
      color: isBlocked ? "text-green-600" : "text-red-600"
    },
    {
      icon: isIgnored ? EyeOff : VolumeX,
      label: isIgnored ? "Stop Ignoring" : "Ignore User",
      onClick: handleIgnore,
      color: isIgnored ? "text-green-600" : "text-orange-600"
    },
    {
      icon: Flag,
      label: "Report User",
      onClick: () => { onReport?.(targetUser); onClose?.(); },
      color: "text-red-700"
    }
  ];

  const menuStyle = showInline ? {} : {
    position: 'fixed',
    left: position.x,
    top: position.y,
    zIndex: 100
  };

  return (
    <div 
      ref={menuRef}
      className="retro-window min-w-[180px] shadow-lg"
      style={menuStyle}
      data-testid="user-action-menu"
    >
      <div className="retro-title-bar py-0.5">
        <span className="text-xs flex items-center gap-1">
          <User className="w-3 h-3" />
          {targetUser?.username}
        </span>
        <button 
          className="retro-button px-1 py-0 text-[10px]"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      
      <div className="bg-[#C0C0C0] p-1">
        {/* Webcam status indicator */}
        {targetUser?.webcam_on && (
          <div className="flex items-center gap-2 p-2 mb-1 bg-green-100 border border-green-400 text-xs">
            <Video className="w-3 h-3 text-green-600" />
            <span className="text-green-700">Webcam is ON</span>
          </div>
        )}
        
        {menuItems.map((item, index) => {
          if (item.divider) {
            return <div key={index} className="border-t border-[#808080] my-1" />;
          }
          
          const Icon = item.icon;
          return (
            <button
              key={index}
              className="w-full flex items-center gap-2 p-2 hover:bg-[#000080] hover:text-white text-left transition-colors"
              onClick={item.onClick}
              data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className={`w-4 h-4 ${item.color}`} />
              <span className="font-arial text-xs">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default UserActionMenu;
